/**
 * What a list screen works out from the rows it was given: the statistics above it, the counts on
 * its tabs, the choices in its filters, and which rows survive the view a person has set up.
 *
 * All of it is arithmetic on an array, with no React and no fetching, for two reasons. It is the
 * part that is easy to get subtly wrong and easy to test exactly; and the order in which it is
 * applied is itself a correctness question — see `shown` below.
 *
 * None of this is safe to run against a page of rows. `inspect()` refuses the whole vocabulary
 * unless the manifest declared `source.complete`, and that refusal is what makes everything here
 * mean what it appears to mean.
 */
import { holds, isApplicable, isQuick, OPERATORS_FOR, rowsOf } from "./manifest.js";
import { foldArabic } from "./arabic.js";
/** The tab that is not a value: every row, drawn first unless the manifest turns it off. */
export const ALL = -1;
/** The one tab the `tabs.closed` values are drawn as. */
export const CLOSED = -2;
/**
 * The tab of `tabs.groups[i]` is `GROUP - i`: a number like the others, so a view keeps holding
 * one, and far enough from ALL and CLOSED that no count of groups reaches them.
 */
export const GROUP = -100;
/** Which of `tabs.groups` a tab index is, or undefined when it is not a group's tab. */
export function groupAt(tabs, index) {
    return index <= GROUP ? tabs?.groups?.[GROUP - index] : undefined;
}
/** The position in `tabs.groups` of the group a value is drawn under, or -1. */
export function groupOf(tabs, value) {
    return (tabs.groups ?? []).findIndex((g) => g.values.some((v) => v === value));
}
export const NO_WHERE = { rel: 'all', items: [] };
export const NOTHING_NARROWED = { tab: ALL, search: '', filters: {}, where: NO_WHERE, sort: '', page: 1 };
/** The view a screen opens on: nothing narrowed, in the manifest's default order. */
export function openingView(manifest) {
    return { ...NOTHING_NARROWED, sort: manifest.sort?.default ?? '' };
}
/**
 * Does this row satisfy every condition? The same test `when` applies to an action — and it is the
 * same code, re-exported rather than written twice, which is the only version of "the same" a
 * comment cannot get wrong.
 */
export { holds } from "./manifest.js";
/**
 * The statistics, counted over every row.
 *
 * Every row, and not the rows a filter left behind: a figure labelled "Members" that fell as
 * somebody typed into the search box would be reporting on the search, under a label that says it
 * is reporting on the workspace.
 */
export function statsOf(manifest, all) {
    return (manifest.stats ?? []).map((stat) => {
        const matchedRows = all.filter((row) => holds(row, stat.where));
        const matched = matchedRows.length;
        let value;
        if (stat.aggregate === 'percent') {
            // No rows at all is 0%, not a division by zero shown as NaN.
            value = all.length ? Math.round((matched / all.length) * 100) : 0;
        }
        else if (stat.aggregate === 'oldest') {
            value = 0;
        }
        else if (stat.aggregate === 'sum') {
            const key = stat.column ?? '';
            value = matchedRows.reduce((total, row) => {
                const n = Number(row[key]);
                return total + (Number.isFinite(n) ? n : 0);
            }, 0);
        }
        else {
            value = matched;
        }
        return {
            key: stat.key, label: stat.label, aggregate: stat.aggregate, value,
            ...(stat.label_key ? { label_key: stat.label_key } : {}),
            ...(stat.aggregate === 'percent' ? { matched, total: all.length } : {}),
            ...(stat.aggregate === 'oldest' ? { since: oldestOf(matchedRows, stat.column ?? '') } : {}),
            ...(stat.detail ? { detail: stat.detail } : {}),
            ...(stat.detail_key ? { detail_key: stat.detail_key } : {}),
            ...(stat.where ? { where: stat.where } : {}),
            ...(stat.icon ? { icon: stat.icon } : {}),
            ...(stat.tone ? { tone: stat.tone } : {}),
        };
    });
}
/** The earliest date or moment a column holds over these rows, as it arrived — null when none holds one. */
function oldestOf(rows, column) {
    let oldest = null;
    for (const row of rows) {
        const cell = row[column];
        const at = typeof cell === 'string' ? Date.parse(cell) : NaN;
        if (Number.isFinite(at) && (!oldest || at < oldest.at))
            oldest = { at, text: cell };
    }
    return oldest?.text ?? null;
}
/**
 * The tabs, with a count each, over every row — for the same reason the statistics are.
 *
 * The values come from the manifest and the counts from the rows, which is the whole point: a
 * declared value with nothing in it still gets a tab reading zero. Taken from the data instead,
 * "Expired" would disappear on the day nothing had expired, which is the day its emptiness is
 * worth saying.
 *
 * Matching is by identity against the declared value, not by text: a tab declared as the number 1
 * is not the tab for the string "1", and this is the one place the distinction survives, because
 * the manifest wrote the value and the row carries the same type it came in as.
 */
export function tabsOf(manifest, all) {
    const tabs = manifest.tabs;
    if (!tabs)
        return [];
    const declared = (tabs.values ?? []).map((value, index) => ({
        index, value, count: all.filter((row) => row[tabs.by] === value).length, ...tabLabel(tabs, value),
    }));
    const countOf = (values) => all.filter((row) => values.some((v) => v === row[tabs.by])).length;
    return withClosed(tabs, withGroups(tabs, declared, countOf), countOf(tabs.closed ?? []), all.length);
}
/**
 * The declared tabs with each group's values folded into the group's one tab, standing where the
 * first of them stood — so «Need follow-up» sits among the states in the order they were declared.
 */
function withGroups(tabs, declared, countOf) {
    if (!tabs.groups?.length)
        return declared;
    const out = [];
    const placed = new Set();
    for (const tab of declared) {
        const g = groupOf(tabs, tab.value);
        if (g < 0) {
            out.push(tab);
            continue;
        }
        if (placed.has(g))
            continue;
        placed.add(g);
        const group = tabs.groups[g];
        out.push({ index: GROUP - g, group: group.key, label: group.label, ...(group.label_key ? { label_key: group.label_key } : {}), count: countOf(group.values) });
    }
    return out;
}
/** Whether a value is one the tabs draw together as «Closed». */
export function isClosed(tabs, value) {
    return (tabs.closed ?? []).some((v) => v === value);
}
/** The declared tabs, the closed values among them folded into one tab at the end, and «All» first. */
function withClosed(tabs, declared, closedCount, allCount) {
    const open = declared.filter((t) => !isClosed(tabs, t.value));
    const folded = tabs.closed?.length ? [...open, { index: CLOSED, count: closedCount }] : declared;
    return tabs.all === false ? folded : [{ index: ALL, count: allCount }, ...folded];
}
function tabLabel(tabs, value) {
    const label = tabs.labels?.[String(value)];
    return label ? { label } : {};
}
/**
 * Tabs read from a catalogue (`tabs.values_from`), made into declared tabs: the catalogue's rows, in
 * its order, become `values`, and each one's `label` names its tab. Every row of the catalogue is a
 * tab — the leave type nobody has taken reads 0 — because the catalogue says which exist, not the
 * rows. A catalogue that could not be read leaves the manifest as it was, and the list draws the
 * "all" tab only: the rows are still true, and a set of tabs guessed from them would not be.
 */
export function resolveTabs(manifest, answer) {
    const tabs = manifest.tabs;
    const from = tabs?.values_from;
    if (!tabs || !from)
        return manifest;
    const rows = rowsOf(answer, { screen: manifest.screen, kind: 'list', source: { path: from.path, ...(from.rows ? { rows: from.rows } : {}) } });
    if (!rows.ok)
        return manifest;
    const values = [];
    const labels = {};
    for (const row of rows.rows) {
        const value = row[from.value];
        if (value === undefined || value === null || typeof value === 'object' || values.includes(value))
            continue;
        values.push(value);
        const label = row[from.label];
        if (label !== undefined && label !== null && label !== '')
            labels[String(value)] = String(label);
    }
    // An empty catalogue — a workspace with no leave type yet — has no tab to offer but "all", which
    // is what an unread one draws. Resolved to `values: []` it would be a manifest declaring no tabs,
    // which inspect refuses, and the whole list would be refused with it.
    if (!values.length)
        return manifest;
    const { values_from: _read, ...rest } = tabs;
    return { ...manifest, tabs: { ...rest, values, labels } };
}
/**
 * Filters whose choices are read from a route (`filters[].values_from`), made into declared ones:
 * each route's rows, in its order, become that filter's `values`. `answers` holds what each route
 * answered, by the filter's column. A route that has not answered, or answered no rows, leaves its
 * filter as it was — offering what the rows in hand hold — rather than a filter with no choices.
 */
export function resolveFilters(manifest, answers) {
    if (!manifest.filters?.some((f) => f.values_from))
        return manifest;
    const filters = manifest.filters.map((filter) => {
        const from = filter.values_from;
        if (!from || !(filter.column in answers))
            return filter;
        const rows = rowsOf(answers[filter.column], { screen: manifest.screen, kind: 'list', source: { path: from.path, ...(from.rows ? { rows: from.rows } : {}) } });
        if (!rows.ok)
            return filter;
        const values = [];
        for (const row of rows.rows) {
            const value = row[from.value];
            if (value === undefined || value === null || typeof value === 'object' || values.includes(value))
                continue;
            values.push(value);
        }
        if (!values.length)
            return filter;
        const { values_from: _read, ...rest } = filter;
        return { ...rest, values };
    });
    return filters.every((f, i) => f === manifest.filters[i]) ? manifest : { ...manifest, filters };
}
/**
 * Whether the catalogue a list's empty state waits on (`empty.catalogue`) answered any rows: the
 * manifest with `none` filled in, or unchanged when the answer is not rows — an answer nobody can
 * read is not an empty catalogue, and the ordinary empty block is drawn rather than a guess.
 */
export function resolveEmpty(manifest, answer) {
    const waits = manifest.empty?.catalogue;
    if (!manifest.empty || !waits)
        return manifest;
    const rows = rowsOf(answer, { screen: manifest.screen, kind: 'list', source: { path: waits.path, ...(waits.rows ? { rows: waits.rows } : {}) } });
    if (!rows.ok)
        return manifest;
    return { ...manifest, empty: { ...manifest.empty, catalogue: { ...waits, none: rows.rows.length === 0 } } };
}
/**
 * The filters, with the choices each one offers.
 *
 * The choices are the distinct values the rows actually carry, so no choice is offered that would
 * empty the table, and a value nobody wrote into the manifest still appears the day it exists. A
 * list column contributes each of its entries, because a filter on roles means "has this role",
 * not "has exactly these roles".
 *
 * They are derived from every row, like everything else here. Derived from what the other filters
 * left, choosing one filter would shorten the next one's list — and a choice that vanishes because
 * of an unrelated choice reads as a bug whichever way it is explained.
 */
export function filtersOf(manifest, all) {
    // A filter that does not apply offers no choices: it is drawn as its words, not as a control.
    return (manifest.filters ?? []).filter(isApplicable).map((filter) => {
        // Declared values come first and in their declared order; the rows may only add to them.
        const seen = (filter.values ?? []).map(String);
        for (const row of all) {
            for (const value of valuesIn(row[filter.column])) {
                if (value !== '' && !seen.includes(value))
                    seen.push(value);
            }
        }
        return { column: filter.column, label: filter.label, options: seen, ...(filter.label_key ? { label_key: filter.label_key } : {}) };
    });
}
/** A cell's values as text: one, or each of them when the cell is a list. */
function valuesIn(cell) {
    if (cell === null || cell === undefined)
        return [];
    return Array.isArray(cell) ? cell.map(String) : [String(cell)];
}
/**
 * The rows a person is actually looking at: the tab, then the search, then each filter.
 *
 * This is the second half of the rule the functions above keep repeating. The statistics, the tab
 * counts and the filter options are computed from `all`; only the table and the "n results" beside
 * it are computed from this. Computing the whole screen from the narrowed rows is the natural
 * mistake, and it produces a screen where every number agrees with itself and none of them means
 * what its label says.
 */
export function shown(manifest, all, view) {
    let rows = all;
    const tabs = manifest.tabs;
    const group = groupAt(tabs, view.tab);
    if (tabs?.values && view.tab === CLOSED) {
        rows = rows.filter((row) => isClosed(tabs, row[tabs.by]));
    }
    else if (tabs && group) {
        rows = rows.filter((row) => group.values.some((v) => v === row[tabs.by]));
    }
    else if (tabs?.values && view.tab !== ALL && view.tab >= 0 && view.tab < tabs.values.length) {
        const value = tabs.values[view.tab];
        rows = rows.filter((row) => row[tabs.by] === value);
    }
    const needle = foldArabic(view.search.trim());
    if (needle && manifest.search?.columns.length) {
        rows = rows.filter((row) => manifest.search.columns.some((key) => valuesIn(row[key]).some((v) => foldArabic(v).includes(needle))));
    }
    for (const filter of manifest.filters ?? []) {
        if (!isApplicable(filter))
            continue;
        const chosen = view.filters[filter.column];
        // Filter values came out of the rows as text, so they are compared as text. Tabs are the other
        // case and are compared by identity: there the value was written in the manifest.
        if (chosen)
            rows = rows.filter((row) => valuesIn(row[filter.column]).includes(chosen));
    }
    if (view.where.items.length) {
        const columns = manifest.columns ?? [];
        rows = rows.filter((row) => matches(row, view.where, columns));
    }
    return sorted(rows, view.sort, manifest.columns ?? []);
}
/**
 * Does the row satisfy the advanced filter? A condition compares by the column's type — a number
 * as a number, a day as a day, text case-insensitively — because "salary > 12000" written as text
 * would put 9000 above 12000. An empty group holds, so a filter with nothing in it narrows nothing.
 */
export function matches(row, node, columns) {
    if ('items' in node) {
        if (!node.items.length)
            return true;
        return node.rel === 'any' ? node.items.some((n) => matches(row, n, columns)) : node.items.every((n) => matches(row, n, columns));
    }
    const type = (columns.find((c) => c.key === node.column)?.type ?? 'text');
    if (!OPERATORS_FOR[type].includes(node.op))
        return false;
    const cell = row[node.column];
    switch (node.op) {
        case 'contains': return text(cell).includes(text(node.value));
        case 'starts': return text(cell).startsWith(text(node.value));
        case 'eq': return NUMERIC.includes(type) ? num(cell) === num(node.value) : text(cell) === text(node.value);
        case 'in': {
            const wanted = (Array.isArray(node.value) ? node.value : [node.value]).map(String);
            return valuesIn(cell).some((v) => wanted.includes(v));
        }
        case 'gt': return num(cell) > num(node.value);
        case 'lt': return num(cell) < num(node.value);
        case 'between': return NUMERIC.includes(type)
            ? num(cell) >= num(node.value) && num(cell) <= num(node.value2)
            : day(cell) >= day(node.value) && day(cell) <= day(node.value2);
        case 'on': return day(cell) === day(node.value);
        case 'before': return day(cell) !== '' && day(cell) < day(node.value);
        case 'after': return day(cell) !== '' && day(cell) > day(node.value);
        case 'is': return cell === (node.value === 'true' ? true : node.value === 'false' ? false : node.value);
    }
}
const NUMERIC = ['number', 'money', 'count', 'bytes', 'percent'];
const text = (v) => foldArabic(v === null || v === undefined ? '' : String(v));
/** A blank is not a number and satisfies no comparison — NaN, which every `<`, `>` and `===` refuses. */
const num = (v) => { if (v === null || v === undefined || v === '')
    return NaN; const n = Number(v); return Number.isFinite(n) ? n : NaN; };
/** The day part of a date or a datetime, so "on 2025-01-01" matches 2025-01-01T09:30. */
const day = (v) => (typeof v === 'string' ? v.slice(0, 10) : '');
/**
 * The rows in the order asked for. Stable, and by the column's type: numbers numerically, days
 * as ISO text, everything else by locale, with a missing value last whichever way the sort goes —
 * a blank is not the smallest value, it is no value.
 */
export function sorted(rows, sort, columns, locale) {
    if (!sort)
        return rows;
    const desc = sort.startsWith('-');
    const key = desc ? sort.slice(1) : sort;
    const type = (columns.find((c) => c.key === key)?.type ?? 'text');
    const none = (v) => v === null || v === undefined || v === '';
    const compare = (a, b) => {
        if (NUMERIC.includes(type))
            return num(a) - num(b);
        if (type === 'boolean')
            return Number(Boolean(a)) - Number(Boolean(b));
        if (type === 'date' || type === 'datetime' || type === 'time')
            return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
        return String(a).localeCompare(String(b), locale);
    };
    return rows.map((row, i) => ({ row, i }))
        .sort((x, y) => {
        const a = x.row[key], b = y.row[key];
        // A blank goes last whichever way the sort runs: it is not the smallest value, it is no value.
        if (none(a) || none(b))
            return none(a) && none(b) ? x.i - y.i : none(a) ? 1 : -1;
        const c = compare(a, b);
        return c === 0 ? x.i - y.i : desc ? -c : c;
    })
        .map((x) => x.row);
}
/**
 * One page of the rows a view left. Without `page` in the manifest the page is all of them: a
 * complete source with twelve journals is shown whole, and this still answers so the footer can
 * say "12 of 12".
 */
export function pageOf(manifest, rows, view) {
    const total = rows.length;
    const size = manifest.page ? (view.size ?? manifest.page.size) : Math.max(total, 1);
    const pages = Math.max(1, Math.ceil(total / size));
    const page = Math.min(Math.max(1, view.page), pages);
    const start = (page - 1) * size;
    const slice = rows.slice(start, start + size);
    return { rows: slice, page, pages, size, from: total ? start + 1 : 0, to: start + slice.length, total };
}
/** How many conditions a where tree holds, for the badge on the button that opens the builder. */
export function conditionCount(node) {
    return 'items' in node ? node.items.reduce((n, i) => n + conditionCount(i), 0) : 1;
}
/**
 * The tree without empty conditions and empty groups, and a group of one collapsed to that one:
 * what is sent and what is drawn as chips, so neither carries a shape the person cannot see.
 */
export function pruned(node) {
    const items = [];
    for (const item of node.items) {
        if ('items' in item) {
            const inner = pruned(item);
            if (inner.items.length === 1)
                items.push(inner.items[0]);
            else if (inner.items.length)
                items.push(inner);
        }
        else if (filled(item))
            items.push(item);
    }
    return { rel: node.rel, items };
}
function filled(c) {
    if (c.value === undefined || c.value === null || c.value === '')
        return false;
    if (Array.isArray(c.value) && !c.value.length)
        return false;
    if (c.op === 'between' && (c.value2 === undefined || c.value2 === null || c.value2 === ''))
        return false;
    return true;
}
/** The tree without the node at `path` (indexes from the root), with any group that emptied removed. */
export function without(node, path) {
    if (path.length === 1)
        return pruned({ ...node, items: node.items.filter((_, i) => i !== path[0]) });
    const [head, ...rest] = path;
    return pruned({ ...node, items: node.items.map((item, i) => (i === head && 'items' in item ? without(item, rest) : item)) });
}
/**
 * The query string a `query` route is asked, from the view. One encoding, readable on the wire:
 * `?sort=-joined_at&q=sara&filter[role]=is:admin&where={"all":[…]}&page=2&size=25`. The quick
 * filters travel as `filter[column]`, the tree as `where`, and the tab as a filter on its column —
 * a tab is a filter the screen drew as a strip.
 */
export function queryFor(manifest, view) {
    const p = new URLSearchParams();
    if (view.sort)
        p.set('sort', view.sort);
    if (view.search.trim())
        p.set('q', view.search.trim());
    const tabs = manifest.tabs;
    if (tabs?.values && view.tab !== ALL && view.tab >= 0 && view.tab < tabs.values.length)
        p.set(`filter[${tabs.by}]`, `is:${String(tabs.values[view.tab])}`);
    for (const filter of manifest.filters ?? []) {
        const chosen = view.filters[filter.column];
        if (chosen && isQuick(filter))
            p.set(`filter[${filter.column}]`, `is:${chosen}`);
    }
    // The closed tab and a group's are several values of one column: each travels as a condition
    // of the tree, `in`.
    const several = view.tab === CLOSED ? tabs?.closed : groupAt(tabs, view.tab)?.values;
    const closedTab = tabs && several?.length ? [{ column: tabs.by, op: 'in', value: several.map(String) }] : [];
    const where = pruned(closedTab.length ? { rel: 'all', items: [...closedTab, ...(view.where.items.length ? [view.where] : [])] } : view.where);
    if (where.items.length)
        p.set('where', JSON.stringify(compact(where)));
    if (manifest.page) {
        p.set('page', String(view.page));
        p.set('size', String(view.size ?? manifest.page.size));
    }
    const out = p.toString();
    return out ? `?${out}` : '';
}
function compact(node) {
    if ('items' in node)
        return { [node.rel]: node.items.map(compact) };
    return { f: node.column, op: node.op, v: node.value2 !== undefined ? [node.value, node.value2] : node.value };
}
/**
 * Whether an empty table is empty because the view narrowed it, or because there is nothing.
 *
 * On a complete source the loaded rows are the whole, so "some loaded, none shown, something
 * filtering" is the answer. On a query source the page in hand says nothing about the whole: a
 * filtered page is empty whether the workspace holds three thousand rows or none. The server's
 * `tabs.all` is the whole (docs/list-standard.md §9); when a route does not carry it, a filtered
 * empty page is taken as narrowed rather than as an empty workspace, because saying "nothing
 * here yet" over a filter that emptied a full list is the worse of the two mistakes.
 */
export function narrowedEmpty(serverSide, loaded, whole, shown, filtering) {
    if (shown > 0 || !filtering)
        return false;
    return serverSide ? (whole ?? 1) > 0 : loaded > 0;
}
/**
 * Whether the list has nothing at all in it — not a view that left nothing, but no row anywhere:
 * a workspace that has not filed one yet. Then the page is the empty block and its action, and
 * nothing else: a tab reading 0 beside «All 0», a card reading 0, a search over nothing and
 * «showing 0 of 0» were five ways of saying what the block says once (HR review, 2026-10-07; RF-15).
 * The tabs and cards keep their zeros on a list that has rows — «Expired 0» is worth saying there.
 *
 * On a complete source that is the rows loaded. On a query source the route's `all` says it; a
 * route that sends none is taken at its word only when the view narrows nothing.
 */
export function vacantList(serverSide, loaded, whole, total, filtering) {
    if (!serverSide)
        return loaded === 0;
    return whole !== undefined ? whole === 0 : total === 0 && !filtering;
}
/**
 * The figures a `query` route counted, in the shape the cards and the tabs draw. The server's
 * totals replace the arithmetic above, because on a page the arithmetic would be about the page.
 */
/** Whether a figure has something to say: not zero, or for `oldest` a moment it counts from. */
function figureSays(stat) {
    return stat.aggregate === 'oldest' ? Boolean(stat.since) : stat.value !== 0;
}
/**
 * The cards drawn now, in their places: a card that `replaces` another is drawn in that card's place
 * while its own figure says something, and the other comes back when it does not; a card drawn only
 * `when: "nonzero"` is left out while it is zero. Every card is counted all the same — only which is
 * drawn changes — so the row of four never grows.
 */
export function drawnStats(manifest, stats) {
    const declared = new Map((manifest.stats ?? []).map((s) => [s.key, s]));
    const byKey = new Map(stats.map((s) => [s.key, s]));
    const standIn = new Map();
    for (const s of stats) {
        const target = declared.get(s.key)?.replaces;
        if (target && byKey.has(target) && figureSays(s))
            standIn.set(target, s);
    }
    const out = [];
    for (const s of stats) {
        const spec = declared.get(s.key);
        if (spec?.replaces && byKey.has(spec.replaces))
            continue;
        const shown = standIn.get(s.key) ?? s;
        if (declared.get(shown.key)?.when === 'nonzero' && !figureSays(shown))
            continue;
        out.push(shown);
    }
    return out;
}
export function statsFromTotals(manifest, totals) {
    return (manifest.stats ?? []).map((stat) => {
        const figure = totals?.stats?.[stat.key];
        return {
            key: stat.key, label: stat.label, aggregate: stat.aggregate, value: typeof figure === 'number' ? figure : 0,
            ...(stat.aggregate === 'oldest' ? { since: typeof figure === 'string' && Number.isFinite(Date.parse(figure)) ? figure : null } : {}),
            ...(stat.label_key ? { label_key: stat.label_key } : {}),
            ...(stat.detail ? { detail: stat.detail } : {}),
            ...(stat.detail_key ? { detail_key: stat.detail_key } : {}),
            ...(stat.where ? { where: stat.where } : {}),
            ...(stat.icon ? { icon: stat.icon } : {}),
            ...(stat.tone ? { tone: stat.tone } : {}),
        };
    });
}
export function tabsFromTotals(manifest, totals) {
    const tabs = manifest.tabs;
    if (!tabs)
        return [];
    const declared = (tabs.values ?? []).map((value, index) => ({ index, value, count: totals?.tabs?.[String(value)] ?? 0, ...tabLabel(tabs, value) }));
    const countOf = (values) => values.reduce((n, v) => n + (totals?.tabs?.[String(v)] ?? 0), 0);
    return withClosed(tabs, withGroups(tabs, declared, countOf), countOf(tabs.closed ?? []), totals?.tabs?.['all'] ?? 0);
}
/**
 * What to call a value: the catalogue's word for it, or the value itself.
 *
 * A status column holding `invited` should read "Invited" in English and «مدعوّ» in Arabic, and an
 * enabled flag shown as a badge should read "Enabled" rather than the "Yes" that a boolean cell
 * says. Both are the same question — what does this value mean, here — so both ask it the same
 * way: `value.{column}.{value}` in the catalogue.
 *
 * A miss falls back to the value as it arrived, visibly. Capitalising the key instead would invent
 * an English word for a value in a language that has none, and would go on doing it convincingly.
 */
export function valueLabel(column, value, i18n) {
    if (value === null || value === undefined)
        return '—';
    if (column.values_key) {
        const own = `${column.values_key}.${String(value)}`;
        const word = i18n.t(own);
        if (word !== own)
            return word;
    }
    const key = `value.${column.key}.${String(value)}`;
    const label = i18n.t(key);
    return label === key ? String(value) : label;
}
/**
 * The columns, each second line worded as its own column words it. A value's quiet line under it
 * (`second`, or a person's `detail`) names another column; when the screen declares that column
 * with `values_key` — a hidden `next_step` worded under `rental.collections.next_step` — the line
 * says the same words the column would (`second_values_key`), not the code. A column that already
 * names `second_values_key`, or whose line's column has no words, is returned as it was.
 */
export function secondWords(columns) {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    return columns.map((c) => {
        const line = c.display === 'person' ? c.detail : c.second;
        const words = line && !c.second_values_key ? byKey.get(line)?.values_key : undefined;
        return words ? { ...c, second_values_key: words } : c;
    });
}
/**
 * A manifest string looked up in the catalogue, with a visible fallback.
 *
 * Same honesty as `valueLabel`: a miss shows the literal (or the key), never an invented English
 * word capitalised from the key. Resolves UI-14 for title, lede, column and stat labels without
 * requiring every module to ship a catalogue on day one.
 */
export function resolveLabel(literal, labelKey, i18n) {
    if (labelKey) {
        const translated = i18n.t(labelKey);
        if (translated !== labelKey)
            return translated;
        if (literal)
            return literal;
        return labelKey;
    }
    return literal ?? '';
}
/**
 * The view to use, given what was last held and which screen is being drawn now.
 *
 * A tab, a search and a set of filters are state, and state outlives the thing it is about unless
 * something says otherwise. Held beside the screen it belongs to and read only while the two still
 * match: moving from People to Roles cannot carry People's tab across and narrow a list somebody
 * believes they are seeing all of.
 *
 * The shell happens to unmount the screen while the next manifest loads, which would reset it
 * anyway — but that is an accident of there being a loading frame in between, and it would stop
 * being true the day somebody keeps the old rows on screen to avoid the flash. A unit drawing two
 * lists of its own in Phase 2 has no such accident to rely on.
 */
export function viewFor(held, screen) {
    return held.screen === screen ? held.view : NOTHING_NARROWED;
}
/** How many data columns a list table draws as heads: five, and three on a phone. */
export const COLUMNS_WIDE = 5;
export const COLUMNS_NARROW = 3;
/**
 * The first `limit` visible columns, in the order the manifest wrote them — except that the column
 * the tabs split on, when it is not among them, takes the last place: the status the tabs are by is
 * what a person looks for first, and a tab reading "Suspended 3" over rows that do not say which
 * three are suspended makes them open every row. A list with no more than `limit` columns folds
 * nothing and is drawn as it always was. `hidden` columns are neither: they are named, not drawn.
 */
export function columnPlan(manifest, limit) {
    const visible = secondWords(manifest.columns ?? []).filter((c) => !c.hidden);
    if (visible.length <= limit)
        return { heads: visible, folded: [] };
    // The pinned columns take their places first; the declaration's order fills the rest.
    const pinned = visible.filter((c) => c.pin);
    const first = [...pinned, ...visible.filter((c) => !c.pin)].slice(0, limit);
    const by = manifest.tabs?.by;
    const byColumn = by && !first.some((c) => c.key === by) ? visible.find((c) => c.key === by) : undefined;
    // The tabs' column replaces the last unpinned head, or the last head when every one is pinned.
    const giving = byColumn ? [...first].reverse().find((c) => !c.pin) ?? first[limit - 1] : undefined;
    const chosen = new Set(byColumn && giving ? [...first.filter((c) => c !== giving), byColumn] : first);
    return { heads: visible.filter((c) => chosen.has(c)), folded: visible.filter((c) => !chosen.has(c)) };
}
/**
 * The view a figure's card opens: the rows it counted. A condition on the column the tabs are by,
 * with one of the tabs' values, is that tab; every other condition goes into the advanced filter
 * as the person could have written it there, so it shows in the chips and clears from them. A
 * figure with no condition counts every row, and its card narrows nothing.
 */
export function viewForStat(manifest, view, stat) {
    if (!stat.where)
        return undefined;
    const conditions = Array.isArray(stat.where) ? stat.where : [stat.where];
    const tabs = manifest.tabs;
    const opening = { ...view, tab: ALL, filters: {}, where: NO_WHERE, page: 1 };
    if (conditions.length === 1 && tabs?.values && conditions[0].key === tabs.by && 'equals' in conditions[0] && !isClosed(tabs, conditions[0].equals) && groupOf(tabs, conditions[0].equals) < 0) {
        const index = tabs.values.findIndex((v) => v === conditions[0].equals);
        if (index >= 0)
            return { ...opening, tab: index };
    }
    // A figure counting exactly a group's values is that group's tab.
    const any = conditions.length === 1 && tabs && conditions[0].key === tabs.by ? conditions[0].equals_any : undefined;
    if (tabs && any) {
        const g = (tabs.groups ?? []).findIndex((group) => group.values.length === any.length && group.values.every((v) => any.some((w) => w === v)));
        if (g >= 0)
            return { ...opening, tab: GROUP - g };
    }
    // Each condition as the column's own operator: a boolean is `is`, a number `eq`, a set `in`.
    const typeOf = (key) => (manifest.columns?.find((c) => c.key === key)?.type ?? 'text');
    const one = (key, value) => ({ column: key, op: typeOf(key) === 'boolean' ? 'is' : 'eq', value: String(value) });
    const items = [];
    for (const c of conditions) {
        if (c.not_equals_any)
            return undefined;
        if (c.equals_any) {
            items.push(OPERATORS_FOR[typeOf(c.key)].includes('in')
                ? { column: c.key, op: 'in', value: c.equals_any.map(String) }
                : { rel: 'any', items: c.equals_any.map((v) => one(c.key, v)) });
        }
        else
            items.push(one(c.key, c.equals));
    }
    return { ...opening, where: { rel: 'all', items } };
}
/**
 * The view a link opens a list on: `tab=<value>` one of its tabs (`closed` the closed tab), and
 * `stat=<key>` the rows one of its figures counts — so a figure elsewhere (a desk tile «leave
 * requests waiting for you») opens the list on exactly the rows it counted, not the whole list.
 * Anything the list does not declare is ignored, and the list opens as it always does.
 */
export function linkedView(manifest, params) {
    const opening = openingView(manifest);
    const stat = params['stat'] ? manifest.stats?.find((s) => s.key === params['stat']) : undefined;
    if (stat?.where) {
        const derived = { key: stat.key, label: stat.label, aggregate: stat.aggregate, value: 0, where: stat.where };
        const v = viewForStat(manifest, opening, derived);
        if (v)
            return v;
    }
    const tabs = manifest.tabs;
    const want = params['tab'];
    if (tabs?.values && want !== undefined) {
        if (want === 'closed' && tabs.closed?.length)
            return { ...opening, tab: CLOSED };
        const g = (tabs.groups ?? []).findIndex((group) => group.key === want);
        if (g >= 0)
            return { ...opening, tab: GROUP - g };
        const index = tabs.values.findIndex((v) => String(v) === want);
        if (index >= 0)
            return { ...opening, tab: index };
    }
    return opening;
}
/**
 * The tabs a person's own list draws (`audience: "self"`, list standard §11.4): a tab only for a state
 * some row is in, and none at all while the rows are in one state or none — six status tabs over one
 * request was the desk's strip on an employee's phone (EMP-18). «All» stays beside two or more, and
 * the tab in view is never taken from under it.
 */
export function selfTabs(manifest, tabs, current) {
    if (manifest.audience !== 'self')
        return tabs;
    const present = tabs.filter((t) => t.index !== ALL && t.count > 0);
    if (present.length < 2 && current === ALL)
        return [];
    return tabs.filter((t) => t.index === ALL || t.count > 0 || t.index === current);
}
//# sourceMappingURL=derive.js.map